import { useState, Fragment } from "react";
import { BiChevronDown, BiChevronUp, BiSolidEditAlt } from "react-icons/bi";
import { FaRegEye } from "react-icons/fa";
import { MdDeleteForever, MdClose } from "react-icons/md";
import { LuArrowUpDown, LucideArrowUpDown } from "lucide-react";

const cellAlign = (a) =>
  a === "left" ? "text-left" : a === "right" ? "text-right" : "text-center";

export default function Table(props) {
  const {
    columns = [],
    data = [],
    noRecordsMessage = "No records found",
    showActions = true,
    onEdit,
    onDelete,
    checkbox,
    onView,
    renderExpansion,
    tableClassName = "",
    expandedRows: externalExpandedRows,
    onExpand,
    onPrint,
    rowIdField = "id", // Default row identity field
    sortConfig, // { key, direction }
    onSort,
    customFooter,
  } = props;
  const [internalExpandedRows, setInternalExpandedRows] = useState({});

  const expandedRows = externalExpandedRows || internalExpandedRows;

  const toggleRow = (id) => {
    const newState = !expandedRows[id];
    if (onExpand) {
      onExpand(id, newState);
    }
    if (!externalExpandedRows) {
      setInternalExpandedRows((prev) => ({ ...prev, [id]: newState }));
    }
  };

  const handleSort = (key) => {
    if (onSort) {
      onSort(key);
    }
  };

  const cols = Array.isArray(columns) ? columns : [];
  const rows = Array.isArray(data) ? data : [];

  const idsOnPage = checkbox?.idField
    ? rows
      .map((r) => r?.[checkbox.idField])
      .filter((x) => x !== undefined && x !== null)
    : [];

  const allSelected =
    idsOnPage.length > 0 &&
    idsOnPage.every((id) => (checkbox?.selected ?? []).includes(id));

  return (
    <div className="relative bg-white border border-gray-200">
      <table className={`w-full text-left border-collapse text-[12px] font-bold ${tableClassName || ""}`}>
        {cols.some((c) => c.w) && (
          <colgroup>
            {checkbox && <col className="w-10" />}
            {cols.map((c, i) => (
              <col key={i} className={c.w ?? "w-auto"} />
            ))}
            {showActions && <col className="w-36" />}
          </colgroup>
        )}

        <thead className="bg-gray-50 text-gray-700 uppercase sticky top-0 z-[20] border-b border-gray-200">
          <tr>
            {renderExpansion && <th className="w-10 sticky left-0 z-20 bg-gray-50 border-r border-gray-200 shadow-[1px_0_0_0_#e5e7eb]"></th>}
            {checkbox && (
              <th className="p-3 text-center align-middle border-r border-gray-200 sticky left-0 z-20 bg-gray-50 shadow-[1px_0_0_0_#e5e7eb]">
                <input
                  type="checkbox"
                  className="w-3.5 h-4 border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  checked={allSelected}
                  onChange={(e) =>
                    checkbox?.onToggleAll?.(idsOnPage, e.target.checked)
                  }
                />
              </th>
            )}
            {cols.map((col, i) => (
              <th
                key={i}
                onClick={() => !col.disableSort && handleSort(col.key)}
                className={`p-3 text-[10px] font-black text-[#475569] uppercase tracking-[1.5px] border-r border-gray-200 last:border-r-0 ${cellAlign(
                  col.align,
                )} ${col.headerClassName || ""} ${!col.disableSort ? "cursor-pointer select-none group" : ""} ${col.sticky ? "sticky z-20 bg-gray-50 shadow-[1px_0_0_0_#e5e7eb]" : ""} ${col.w || ""}`}
                style={col.sticky ? { left: col.left ?? 0 } : {}}
              >
                <div className={`flex items-center gap-1 ${col.align === "right" ? "justify-end" : col.align === "center" ? "justify-center" : "justify-start"}`}>
                  {col.label}
                  {!col.disableSort && (
                    <LucideArrowUpDown
                      size={12}
                      className={`transition-colors ${sortConfig?.key === col.key
                        ? "text-primary opacity-100"
                        : "opacity-30 group-hover:opacity-30"
                        }`}
                    />
                  )}
                </div>
              </th>
            ))}
            {showActions && (
              <th className="p-3 text-[10px] font-black text-[#475569] uppercase tracking-[1.5px] text-center border-l border-gray-200">
                Actions
              </th>
            )}
          </tr>
          {props.onFilterChange && (
            <tr className="bg-white border-b border-gray-200">
              {renderExpansion && <th className="p-2 border-r border-gray-200 sticky left-0 z-20 bg-white shadow-[1px_0_0_0_#e5e7eb]"></th>}
              {checkbox && <th className="p-2 border-r border-gray-200 sticky left-0 z-20 bg-white shadow-[1px_0_0_0_#e5e7eb]"></th>}
              {cols.map((col, i) => (
                <th
                  key={i}
                  className={`p-1 border-r border-gray-200 last:border-r-0 ${col.sticky ? "sticky z-20 bg-white shadow-[1px_0_0_0_#e5e7eb]" : ""} ${col.w || ""}`}
                  style={col.sticky ? { left: col.left ?? 0 } : {}}
                >
                  <div className="relative flex items-center group">
                    <input
                      type="text"
                      placeholder={`Filter ${col.label}...`}
                      className="w-full pl-2 pr-6 py-1 text-[10px] font-normal border border-gray-200 rounded focus:border-blue-400 focus:outline-none placeholder:text-gray-300 normal-case"
                      value={props.filters?.[col.key] || ""}
                      onChange={(e) => props.onFilterChange(col.key, e.target.value)}
                    />
                    {props.filters?.[col.key] && (
                      <button
                        onClick={() => props.onFilterChange(col.key, "")}
                        className="absolute right-1 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <MdClose size={12} />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              {showActions && <th className="p-2 border-l border-gray-200"></th>}
            </tr>
          )}
        </thead>

        <tbody className="divide-y divide-gray-200 text-gray-700">
          {rows.length > 0 ? (
            rows.map((row, idx) => {
              const rowId = row[rowIdField] ?? row?.pO_No ?? row?.PO_No ?? row?.dieId ?? row?.id ?? idx;
              const isExpanded = !!expandedRows[rowId];
              const customRowClass = props.getRowClassName ? props.getRowClassName(row, idx) : "";
              return (
                <Fragment key={row?.id ?? row?.key ?? idx}>
                  <tr
                    className={`hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${customRowClass}`}
                  >
                    {renderExpansion && (
                      <td className={`p-2.5 text-center border-r border-gray-200 sticky left-0 z-10 ${customRowClass || "bg-white"} shadow-[1px_0_0_0_#e5e7eb]`}>
                        <button
                          type="button"
                          onClick={() => toggleRow(rowId)}
                          className="text-primary hover:bg-gray-100 p-1"
                        >
                          {isExpanded ? (
                            <BiChevronUp size={18} />
                          ) : (
                            <BiChevronDown size={18} />
                          )}
                        </button>
                      </td>
                    )}
                    {checkbox && (
                      <td className={`p-2.5 text-center border-r border-gray-200 sticky left-0 z-10 ${customRowClass || "bg-white"} shadow-[1px_0_0_0_#e5e7eb]`}>
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 border-gray-300 text-primary focus:ring-primary cursor-pointer"
                          checked={(checkbox?.selected ?? []).includes(
                            row[checkbox.idField],
                          )}
                          onChange={(e) =>
                            checkbox?.onToggle?.(
                              row[checkbox.idField],
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                    )}

                    {cols.map((col, cidx) => {
                      const val = row[col.key];
                      const content =
                        col.type === "status" ? (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-tighter border ${String(val).toLowerCase() === "done" ||
                              String(val).toLowerCase() === "active"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-gray-50 text-gray-600 border-gray-200"
                              }`}
                          >
                            {val}
                          </span>
                        ) : col.render ? (
                          col.render(val, row, idx)
                        ) : (
                          val
                        );

                      return (
                        <td
                          key={cidx}
                          className={`p-2.5 align-middle whitespace-nowrap border-r border-gray-200 last:border-r-0 ${cellAlign(
                            col.align,
                          )} ${col.className || ""} ${col.sticky ? `sticky z-10 ${customRowClass || "bg-white"} shadow-[1px_0_0_0_#e5e7eb]` : ""} ${col.w || ""}`}
                          style={col.sticky ? { left: col.left ?? 0 } : {}}
                        >
                          {content}
                        </td>
                      );
                    })}

                    {showActions && (
                      <td className="p-2 text-center border-l border-gray-200">
                        <div className="flex items-center justify-center gap-1">
                          {onEdit && (
                            <button
                              title="Edit"
                              className="px-2.5 py-1 border border-gray-200 text-blue-600 hover:bg-blue-50 transition-all text-[10px] font-black uppercase tracking-wider rounded-none"
                              onClick={() => onEdit(row)}
                            >
                              Edit
                            </button>
                          )}
                          {onDelete && (
                            <button
                              title="Delete"
                              className="px-2.5 py-1 border border-gray-200 text-red-600 hover:bg-red-50 transition-all text-[10px] font-black uppercase tracking-wider rounded-none"
                              onClick={() => onDelete(row)}
                            >
                              Delete
                            </button>
                          )}
                          {onView && (
                            <button
                              title="View"
                              className="px-2.5 py-1 border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all text-[10px] font-black uppercase tracking-wider rounded-none"
                              onClick={() => onView(row)}
                            >
                              View
                            </button>
                          )}
                          {onPrint && (
                            <button
                              title="Print"
                              className="px-2.5 py-1 border border-gray-200 text-green-600 hover:bg-green-50 transition-all text-[10px] font-black uppercase tracking-wider rounded-none"
                              onClick={() => onPrint(row)}
                            >
                              Print
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  {isExpanded && renderExpansion && (
                    <tr>
                      <td
                        colSpan={
                          cols.length +
                          (showActions ? 1 : 0) +
                          (checkbox ? 1 : 0) +
                          (renderExpansion ? 1 : 0)
                        }
                        className="bg-gray-50 p-2 border-b border-gray-200"
                      >
                        {renderExpansion(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={
                  cols.length + (showActions ? 1 : 0) + (checkbox ? 1 : 0) + (renderExpansion ? 1 : 0)
                }
                className="text-center py-20 text-gray-500 font-medium"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-4 border border-gray-100">
                    <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="text-[14px] font-black tracking-[1.5px] text-gray-800 uppercase mb-1">
                    {noRecordsMessage}
                  </h3>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                    Try adjusting your filters or search query
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
        {props.footer && !customFooter && (
          <tfoot className="sticky bottom-[-1px] z-10 bg-gray-50/90 backdrop-blur-sm border-t-2 border-gray-200 -2px_10px_rgba(0,0,0,0.02)]">
            <tr className="divide-x divide-gray-200 font-black text-slate-800">
              {renderExpansion && <td className="w-10"></td>}
              {checkbox && <td className="w-10"></td>}
              {cols.map((col, i) => (
                <td key={i} className={`p-2.5 text-[11px] align-middle ${cellAlign(col.align)}`}>
                  {props.footer[col.key]}
                </td>
              ))}
              {showActions && <td className="w-36"></td>}
            </tr>
          </tfoot>
        )}
        {customFooter && (
          <tfoot className="sticky bottom-[-1px] z-10 bg-white border-t border-gray-200">
            {customFooter}
          </tfoot>
        )}
      </table>
    </div>
  );
}
